import { useEffect } from "react";

import { Button, VolumeUpSVG } from "@newsela/angelou";

import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import ReadAloudAudio from "@/view/ReadAloudControls/ReadAloudAudio";
import useReadAloudAudio from "@/view/ReadAloudControls/useReadAloudAudio";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import { Letter } from "@/view/QuizQuestion/constants";
import { textCss, answerCss } from "./ReadAloudButton.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

export const ButtonTypes = { question: "question", answer: "answer" } as const;

export interface ReadAloudButtonProps {
  buttonContentMode: (typeof ReadAloudContentModes)[keyof typeof ReadAloudContentModes];
  position?: `${bigint}` | Letter;
  setCurrentReadAloudChoice?: () => void;
  text: INode | string | null;
  type: (typeof ButtonTypes)[keyof typeof ButtonTypes];
}

const ReadAloudButton = ({
  buttonContentMode,
  setCurrentReadAloudChoice,
  type,
  position,
  text,
}: ReadAloudButtonProps) => {
  const {
    readAloudContentMode,
    setReadAloudContentMode,
    currentSpeech,
    setCurrentSpeech,
  } = useReadAloudMode();

  const speech = useReadAloudAudio({
    text,
  });
  const { audioState, isPlaying, pauseAudio, restartAudio } = speech;

  useEffect(() => {
    if (readAloudContentMode !== buttonContentMode) {
      pauseAudio();
    }
  }, [readAloudContentMode]);

  // TODO: LESS-3618 -- Simplify this to only expect prosemirror objects once we have listen mode for
  // multiple choice options implemented
  const textHasChanged = (
    text: INode | string | null,
    audioStateText: INode | string | null,
  ) => {
    if (typeof text === "object") {
      return JSON.stringify(text) !== JSON.stringify(audioStateText);
    } else if (typeof text === "string") {
      return text !== audioStateText;
    } else {
      return false;
    }
  };

  const handleClick = () => {
    if (buttonContentMode === ReadAloudContentModes.ANSWER) {
      setCurrentReadAloudChoice && setCurrentReadAloudChoice();
    }
    /* This button is shared across different QuizQuestionTexts
    reset if we're on a different question (the text changed) */
    if (textHasChanged(text, audioState.text)) {
      speech.resetAudio();
    }

    if (!isPlaying) {
      /* Prevent playing overlapping answers by
      pausing previous answer's read aloud first */
      if (
        readAloudContentMode === buttonContentMode &&
        currentSpeech &&
        textHasChanged(text, currentSpeech?.audioState?.text)
      ) {
        currentSpeech.pauseAudio();
      }
      setCurrentSpeech(speech);
      restartAudio();
      setReadAloudContentMode(buttonContentMode);
    } else {
      pauseAudio();
    }
  };

  return (
    <>
      <ButtonWithTrackEvent
        __cssFor={{
          root: [textCss, ...[type === ButtonTypes.answer ? [answerCss] : []]],
        }}
        icon={{
          SvgComponent: VolumeUpSVG,
          alignment: Button.iconAlignments.left,
        }}
        onClick={() => handleClick()}
        flavor={
          type === ButtonTypes.answer
            ? Button.flavors.tertiary
            : Button.flavors.incognito
        }
        actionName={actionNames.interact}
        objectType={
          type === ButtonTypes.answer
            ? objectTypes.quizAnswer
            : objectTypes.quizQuestion
        }
        streamProperties={{
          description: `Jump to read aloud for ${type} ${position}`,
        }}
        dataProps={{ "data-qa-selector": `read-aloud-quiz-${type}` }}
      >
        {isPlaying ? <ReadAloudAudio speech={speech} /> : null}
        Read {type} {position}
      </ButtonWithTrackEvent>
    </>
  );
};

export default ReadAloudButton;
